import { toast as sonner } from 'sonner';

/**
 * The app's one toast API, built on Sonner. Import `toast` from here and nowhere else; Sonner is not imported
 * outside this file and ToastProvider.
 *
 *   toast.success('Saved');
 *   const id = toast.loading('Uploading...');
 *   toast.update(id, 'Uploaded', { type: 'success' });
 *   toast.dismiss(id);
 */

// How long each kind stays on screen
const DURATION = { success: 4000, error: 5000, info: 4000, warning: 4500 };

/**
 * Options older call sites passed to react-hot-toast and react-toastify. Where a toast appears and how it looks is
 * decided once by ToastProvider, so `position`, `style` and `iconTheme` are dropped. `autoClose` is the old name for
 * `duration`, and `autoClose: false` means the toast stays until it is dismissed.
 */
const normalize = (kind, options = {}) => {
  // eslint-disable-next-line no-unused-vars
  const { autoClose, duration, position, style, iconTheme, ...rest } = options;
  const resolved = autoClose === false ? Infinity : (autoClose ?? duration ?? DURATION[kind]);
  return resolved === undefined ? rest : { ...rest, duration: resolved };
};

export const toast = (message, options) => sonner(message, normalize('info', options));

toast.success = (message, options) => sonner.success(message, normalize('success', options));
toast.error = (message, options) => sonner.error(message, normalize('error', options));
toast.info = (message, options) => sonner.info(message, normalize('info', options));
toast.warning = (message, options) => sonner.warning(message, normalize('warning', options));
toast.warn = toast.warning;
toast.loading = (message = 'Loading...', options) => sonner.loading(message, normalize('loading', options));

/** Shows a loading toast, then turns it into a success or error one when the promise settles. */
toast.promise = (promise, messages = {}, options) => sonner.promise(promise, {
  loading: messages.loading || 'Loading...',
  success: messages.success || 'Success!',
  error: messages.error || 'Something went wrong',
  ...normalize('promise', options),
});

/** Changes a toast that is already on screen, for example a loading toast that has finished. */
toast.update = (id, message, { type = 'info', ...options } = {}) =>
  (sonner[type] || sonner)(message, { ...normalize(type, options), id });

toast.dismiss = (id) => sonner.dismiss(id);
toast.dismissAll = () => sonner.dismiss();

/**
 * A message fit to show a user for a failed request: the server's own message when it sent one, otherwise the
 * fallback. Raw transport errors ("Request failed with status code 500", "Network Error") are replaced by the
 * fallback.
 */
export const errorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  const message = error?.response?.data?.message || error?.message;
  if (!message || /status code|network error|timeout|failed to fetch/i.test(message)) {
    return fallback;
  }
  return message;
};

export default toast;
